import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";
import APIGroupIdSub from "./groupId/client";

export default class APIGroups extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  groupId = new APIGroupIdSub(this._client);

  createGroup = async (
    body: types.GroupCreate,
  ): Promise<types.GroupResponse> => {
    let res = await this._fetch({
      url: `/groups/`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getGroupId = async (groupId: UUID): Promise<types.GroupResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putGroupId = async (
    groupId: UUID,
    { ...body }: types.GroupCreate,
  ): Promise<types.GroupResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  deleteGroupId = async (groupId: UUID): Promise<any> => {
    let res = await this._fetch({
      url: `/groups/${groupId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getMyGroups = async (): Promise<types.GroupResponse[]> => {
    let res = await this._fetch({
      url: `/groups/my-groups`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getCompanyGroups = async (): Promise<types.GroupResponse[]> => {
    let res = await this._fetch({
      url: `/groups/company`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  getGroupOptions = async (): Promise<types.GroupOption[]> => {
    let res = await this._fetch({
      url: `/groups/options`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postAssignChannel = async (
    groupId: UUID,
    body: types.AssignChannelRequest,
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/assign-channel`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  postRemoveChannel = async (
    groupId: UUID,
    body: types.AssignChannelRequest,
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/remove-channel`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putMemberRole = async (
    groupId: UUID,
    userId: UUID,
    body: types.UpdateMemberRoleRequest,
  ): Promise<any> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/members/${userId}/role`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  putOwner = async (groupId: UUID, body: { user_id: UUID }): Promise<void> => {
    await this._fetch({
      url: `/groups/${groupId}/owner`,
      method: "PUT",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // ---- Group-scoped account administration (Agency manager / owner) ----

  // Create a new account directly inside a group. The server emails a
  // set-password link (no password is sent). 409 on duplicate email/phone.
  createUser = async (
    groupId: UUID,
    body: types.GroupUserCreate,
  ): Promise<types.GroupUserCreateResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/users`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Remove a user from the group; archives the account if it has no remaining
  // group ties. Returns `{ archived }`.
  removeUser = async (
    groupId: UUID,
    userId: UUID,
  ): Promise<types.GroupUserRemoveResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/members/${userId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Email a password-reset link to a group member (link only).
  sendUserRecovery = async (
    groupId: UUID,
    userId: UUID,
  ): Promise<types.GroupUserRecoveryResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/users/${userId}/send-recovery`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Edit a group member's identity (name / email / phone / language). 409 on
  // duplicate email/phone; 403/404 when out of the HR admin's scope.
  updateUser = async (
    groupId: UUID,
    userId: UUID,
    body: types.CompanyUserUpdate,
  ): Promise<types.UserResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/users/${userId}`,
      method: "PATCH",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Set a group member's password directly, or generate a temporary one when
  // `password` is omitted (returned once). No impersonation.
  resetUserPassword = async (
    groupId: UUID,
    userId: UUID,
    body: { password?: string | null },
  ): Promise<types.ResetPasswordResponse> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/users/${userId}/reset-password`,
      method: "POST",
      body: body,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
